import { ItemPreviewState } from "@/components/item-preview/item-preview-state";
import { HomeReminderShillActions } from "@/components/item-list/rows/components/home-reminder-shill-actions";
import { ItemListRowFeaturedContent } from "@/components/item-list/rows/components/item-list-row-featured-content";
import { MDSRowItemAppearance } from "@/mds/components/lists/row-item/row-item.types";
import {
  ItemListItemRowData,
  ItemListRowType,
} from "@/components/item-list/types/item-list-row-types";
import { ItemListItemKind, RowStatus } from "@/components/item-list/types/item-list-item-types";
import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import { generateShortDateString } from "@/domains/date/date";
import { MDSIconKind, MDSMenuContentList } from "@/mds";
import { AppStore } from "@/store/AppStore";
import { INoteObservable } from "@/store/note";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { DateTime } from "luxon";

const AWAITING_INPUT_LABEL = "Awaiting Input";

export const getItemListRowForNote = ({
  dateTime,
  dropdown,
  inMainPanel,
  listState,
  noteObservable,
  store,
  trackClick,
  tabId,
  appearance,
  disablePreview = false,
  showReminderShill = false,
}: {
  dateTime: Date | string;
  dropdown?: MDSMenuContentList;
  inMainPanel: boolean;
  listState?: ListStateObservable;
  noteObservable: INoteObservable;
  store: AppStore;
  trackClick?: () => void;
  tabId?: string;
  appearance: MDSRowItemAppearance;
  disablePreview?: boolean;
  showReminderShill?: boolean;
}) => {
  const noteId = noteObservable.id;
  const dateLabel = resolveDateLabel({ dateTime });
  const status = resolveRowStatus({ appearance });

  const isLoading = noteObservable.collectionList?.isLoading;
  const collections = noteObservable.collectionList?.allCollections || [];

  const suggestionData = noteObservable.suggestionData;
  const primaryLabel = suggestionData?.suggested_primary_label || noteObservable.title;
  const secondaryLabel =
    suggestionData?.suggested_secondary_label ||
    noteObservable.secondaryTitle ||
    "No additional text";

  const row: ItemListItemRowData = {
    type: ItemListRowType.Item,
    key: noteObservable.id,
    payload: {
      id: noteObservable.id,
      kind: ItemListItemKind.Note,
      titleIcon: noteObservable.isShared ? { kind: MDSIconKind.Shared } : undefined,
      appearance,
      createPreviewState: disablePreview
        ? undefined
        : () =>
            new ItemPreviewState({
              store,
              id: noteId,
              kind: ItemListItemKind.Note,
            }),
      label: primaryLabel,
      onClick: ({ event }) => {
        store.navigation.goToNote({ noteId, event, activeTabId: tabId });
        trackClick?.();
      },
      sharedBy: noteObservable.sharedBy,
      lastAction: noteObservable.lastAction,
      dateLabel,
      status,
      dropdown,
      listState,
      chips: () => {
        const sortedCollections = sortCollectionsForChips(collections);
        if (sortedCollections.length === 0) return null;
        return <ItemListRowFeaturedContent inline collections={sortedCollections} />;
      },
      extraRow: {
        id: `note-${noteId}-content`,
        content: () => (
          <>
            <ItemListRowFeaturedContent
              snippet={[{ text: secondaryLabel }]}
              isLoading={isLoading}
            />
            {showReminderShill && noteObservable.shouldShowReminderShill && (
              <HomeReminderShillActions noteObservable={noteObservable} />
            )}
          </>
        ),
      },
    },
  };
  if (!inMainPanel) {
    row.payload.extraRow = {
      id: `note-${noteId}-content`,
      content: () => <ItemListRowFeaturedContent snippet={[{ text: dateLabel }]} />,
    };
    row.payload.onClick = () => {
      store.navigation.goToNote({ noteId });
      trackClick?.();
    };
    row.payload.sharedBy = undefined;
    row.payload.dateLabel = undefined;
    row.payload.status = undefined;
    row.payload.dropdown = undefined;
    row.payload.listState = undefined;
  }
  return row;
};

const resolveDateLabel = ({ dateTime }: { dateTime: Date | string }) =>
  generateShortDateString(
    typeof dateTime === "string" ? DateTime.fromISO(dateTime) : DateTime.fromJSDate(dateTime)
  );

/**
 * Status appearances render an icon + full label in the metadata column instead of a
 * date (MEM-17213). Colors come from MDS tokens: emphasis (blue) for in-progress states
 * and danger (red) for failures, matching the Figma `icon/emphasis` and `icon/danger`
 * variables. Returns undefined for plain rows, which keep their date.
 */
const resolveRowStatus = ({
  appearance,
}: {
  appearance: MDSRowItemAppearance;
}): RowStatus | undefined => {
  switch (appearance) {
    case MDSRowItemAppearance.ActiveSuggestion:
      return {
        label: AWAITING_INPUT_LABEL,
        iconKind: MDSIconKind.CircleExclamation,
        tone: "emphasis",
      };
    case MDSRowItemAppearance.AudioRecordingRecording:
      return { label: "Recording", iconKind: MDSIconKind.Microphone, tone: "emphasis" };
    case MDSRowItemAppearance.AudioRecordingUploading:
      return { label: "Uploading", iconKind: MDSIconKind.CloudArrowUp, tone: "emphasis" };
    case MDSRowItemAppearance.AudioRecordingProcessing:
      return { label: "Processing", iconKind: MDSIconKind.Spinner, tone: "emphasis" };
    case MDSRowItemAppearance.AudioRecordingUploadingFailed:
      return { label: "Uploading Error", iconKind: MDSIconKind.CircleExclamation, tone: "danger" };
    case MDSRowItemAppearance.AudioRecordingProcessingFailed:
      return { label: "Processing Error", iconKind: MDSIconKind.CircleExclamation, tone: "danger" };
    default:
      return undefined;
  }
};
